import { searchEvent } from "@/lib/atInternetTags";
import { dayjs } from "@/lib/dayjs";
import { canUseSearchInsights } from "@/lib/searchInsightsConsent";
import type { SearchData } from "front-search-schema/algolia";
import { atom, computed } from "nanostores";
import type { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import type { ReadonlyURLSearchParams } from "next/navigation";
import { unique } from "radash";
import searchInsights from "search-insights";
import { searchTravelClient } from "./searchTravelClient";
import type { ConfigValue, FixedValue, ProductListValues, SelectedValue, Value, ValueType } from "./types";
import { getPathname } from "./url";

export const valuesStore = atom<Array<Value>>([]);
export const fixedValuesStore = atom<Array<Array<FixedValue>>>([]);
export const configValuesStore = atom<Array<ConfigValue>>([]);
export const productListValuesStore = atom<ProductListValues | null>(null);
export const termStore = atom<string>("");
export const selectedValueStore = atom<SelectedValue | null>(null);

export const loadingStore = atom<boolean>(false);
export const dataStore = atom<SearchData | null>(null);

export const isSearchPopupOpenStore = atom(false);

export const fitSearchModeStore = atom<boolean>(false);

export const getSearchBasePath = () => (fitSearchModeStore.get() ? "/sur-mesure/individuel/recherche/" : "/recherche/");

export const paramsStore = atom<Record<string, string | Array<string> | undefined> | null>(null);
export const searchParamsStore = atom<ReadonlyURLSearchParams | null>(null);
export const pathnameStore = atom<string | null>(null);

export const filteredValuesStore = (type: ValueType) => {
  return computed(valuesStore, (values) => {
    return values.filter((v) => v.type === type);
  });
};

export const sortStore = computed(configValuesStore, (config) => config.find((v) => v.type === "sort") || null);
export const pageStore = computed(configValuesStore, (config) => config.find((v) => v.type === "page") || null);
export const datesStore = computed(configValuesStore, (config) => {
  const from = config.find((v) => v.type === "gte") || null;
  const to = config.find((v) => v.type === "lte") || null;

  if (from && to) {
    return {
      from: dayjs(from.value),
      to: dayjs(to.value),
    };
  }

  return null;
});
export const garantiStore = computed(valuesStore, (values) => {
  return values.find((v) => v.type === "garanti")?.value === "true";
});
export const fitTemplateStore = computed(valuesStore, (values) => {
  return values.find((v) => v.type === "isFitTemplate")?.value === "true";
});

export const search = async () => {
  loadingStore.set(true);

  const values = valuesStore.get();
  const fixedValues = fixedValuesStore.get();
  const term = termStore.get();

  const data = await searchTravelClient(term, fitSearchModeStore.get());

  const allValues = [...values, ...fixedValues.flat()];

  searchEvent({
    event: "search.query",
    search_term: term,
    search_durations: allValues.filter((v) => v.type === "durations").map((v) => v.value),
    search_themes: allValues.filter((v) => v.type === "themes").map((v) => v.value),
    search_rythmes: allValues.filter((v) => v.type === "rythme").map((v) => v.value),
    search_destinations: allValues.filter((v) => v.type === "destinations").map((v) => v.value),
    search_speakers: allValues.filter((v) => v.type === "speakers").map((v) => v.value),
    search_budgets: allValues.filter((v) => v.type === "budgets").map((v) => v.value),
  });

  dataStore.set(data);

  loadingStore.set(false);
};

export const setTerm = (term: string) => {
  termStore.set(term);
  valuesStore.set([]);
  configValuesStore.set([]);
  search();
};

export const setTypeValues = (type: ValueType, values: Array<Value>) => {
  const oldValues = valuesStore.get().filter((v) => v.type !== type);
  const newValues = [...oldValues, ...values];
  const facetes = newValues.map((value) => `${value.type}:"${value.value}"`);

  if (canUseSearchInsights()) {
    searchInsights("clickedFilters", {
      index: "search_travels",
      eventName: "filters",
      filters: facetes,
    });
  }
  valuesStore.set(newValues);
};

export const addSelectedValue = (value: SelectedValue, router: AppRouterInstance) => {
  selectedValueStore.set(value);
  isSearchPopupOpenStore.set(false);
  termStore.set("");
  valuesStore.set([value]);
  configValuesStore.set([]);

  updateUrl(router, true);
};

export const replaceAll = (
  values: Array<Value>,
  fixedValues: Array<Array<FixedValue>>,
  configValues: Array<ConfigValue>,
  productListValues: ProductListValues | null,
  term: string,
  selectedValue: SelectedValue | null,
  data: SearchData,
) => {
  if (selectedValue) {
    const v = unique([...values, ...fixedValues.flat(), selectedValue], (v) => v.value);
    valuesStore.set(v);
  } else {
    const v = unique([...values, ...fixedValues.flat()], (v) => v.value);
    valuesStore.set(v);
  }

  fixedValuesStore.set(fixedValues);
  configValuesStore.set(configValues);
  productListValuesStore.set(productListValues);
  termStore.set(term);
  selectedValueStore.set(selectedValue);
  dataStore.set(data);
};

export const resetSearchStore = () => {
  valuesStore.set([]);
  fixedValuesStore.set([]);
  configValuesStore.set([]);
  productListValuesStore.set(null);
  termStore.set("");
  selectedValueStore.set(null);
  dataStore.set(null);
};

const searchUrls = ["/recherche", "/themes", "/bon-plan", "/voyages-culturels/", "/voyages"];
const fitSearchUrls = [
  "/sur-mesure/individuel/recherche",
  "/sur-mesure/individuel/themes",
  "/sur-mesure/individuel/destinations",
];
export const updateUrl = (router: AppRouterInstance, forceRedirect = false) => {
  const term = termStore.get();
  const values = valuesStore.get();
  const configValues = configValuesStore.get();
  const selectedValue = selectedValueStore.get();
  const params = paramsStore.get();

  const basePath = getSearchBasePath();
  const allowedUrls = fitSearchModeStore.get() ? fitSearchUrls : searchUrls;

  let pathname = getPathname(pathnameStore.get() || basePath);

  if (!allowedUrls.some((url) => pathname.startsWith(url))) {
    pathname = basePath;
  }

  if (forceRedirect) {
    pathname = basePath;
  }

  const pageValue = configValues.find((v) => v.type === "page");
  const configs = configValues.filter((v) => v.type !== "page");

  if (pageValue && params?.page !== pageValue.value && pageValue.value !== "1") {
    pathname = `${pathname}${pageValue.value}/`;
  }

  const urlParams = new URLSearchParams();

  if (term !== "") {
    urlParams.set("term", term);
  }

  if (selectedValue) {
    urlParams.set("selectedValue", `${selectedValue.type}|${selectedValue.value}`);
  }

  for (const value of values) {
    urlParams.append(value.type, value.value);
  }

  for (const value of configs) {
    if (value.type === "lte" || value.type === "gte") {
      urlParams.set(value.type, dayjs(value.value).format("DD-MM-YYYY"));
    } else {
      urlParams.set(value.type, value.value);
    }
  }

  router.push(`${pathname}?${urlParams.toString()}`);
  router.refresh();
};

export const setDates = (newConfigValues: ConfigValue[] | null) => {
  if (newConfigValues === null) {
    configValuesStore.set(configValuesStore.get().filter((v) => v.type !== "lte" && v.type !== "gte"));
  } else {
    configValuesStore.set([
      ...configValuesStore.get().filter((v) => v.type !== "lte" && v.type !== "gte"),
      ...newConfigValues,
    ]);
  }
};

export const setConfigValue = (configValue: ConfigValue) => {
  configValuesStore.set([...configValuesStore.get().filter((v) => v.type !== configValue.type), configValue]);
};
